import { useQueryClient } from '@tanstack/react-query'
import { can, type Grants, type Permission } from '@church/permissions'
import { useCallback } from 'react'
import { useAuthClient } from '../auth-context.tsx'
import { sessionKeys } from './keys.ts'
import { useMe, type Me } from './useMe.ts'

/**
 * The session view every screen consumes: signed-in state, user, active org,
 * and permission checks. One hook on both platforms; markup differs, this
 * doesn't (01-ARCHITECTURE.md §2).
 */
export function useSession() {
  const query = useMe()

  return {
    /** undefined while loading, null signed out, Me when signed in. */
    me: query.data,
    isLoading: query.isPending,
    isSignedIn: !!query.data,
    refetch: query.refetch,
  }
}

/**
 * `can()` bound to the active org's grants. Cosmetic on the client — the
 * server enforces — so returning `false` while loading is always safe: UI
 * appears when the grants do.
 */
export function usePermissions() {
  const { me } = useSession()

  const grants: Grants = {
    permissions: (me?.grants.permissions ?? []) as Permission[],
    campusIds: me?.grants.campusIds ?? null,
  }

  // Re-memoized when the serialized grants change — object identity from the
  // query cache is not stable across refetches, the contents are what matter.
  const grantsKey = JSON.stringify(grants)
  const check = useCallback(
    (permission: Permission, scope?: { campusId?: string }) => can(grants, permission, scope),
    [grantsKey],
  )

  return { can: check, grants }
}

/** The active org's membership entry — org identity for shells and switchers. */
export function useOrg(): {
  org: Me['memberships'][number] | null
  memberships: Me['memberships']
  isLoading: boolean
} {
  const { me, isLoading } = useSession()
  const memberships = me?.memberships ?? []
  return {
    org: memberships.find((m) => m.organizationId === me?.activeOrganizationId) ?? null,
    memberships,
    isLoading,
  }
}

/**
 * Switches the active organization (better-auth `set-active`) and drops every
 * cached query: all server state on screen belongs to the previous org.
 */
export function useSwitchOrg() {
  const authClient = useAuthClient()
  const queryClient = useQueryClient()

  return useCallback(
    async (organizationId: string) => {
      const { error } = await authClient.organization.setActive({ organizationId })
      if (error) throw new Error(error.message ?? 'Could not switch organization')
      await queryClient.cancelQueries()
      queryClient.clear()
      await queryClient.refetchQueries({ queryKey: sessionKeys.me() })
    },
    [authClient, queryClient],
  )
}
